// Shared theme + font tokens for the Pro landing page builder.
// Used by both the editor (LandingPageEditor.jsx) and the public renderer
// (CoachPublic.jsx) so they stay in sync.

export const LANDING_THEMES = {
    ocean: {
        id: 'ocean',
        name: 'Ocean',
        description: 'Classic SplashPad — bright cyan over deep navy.',
        // Hero gradient — ALWAYS dark so white text reads cleanly
        heroGradient: 'linear-gradient(135deg, #0a1628 0%, #1a3a5c 55%, #0b4d6b 100%)',
        // Solid dark overlay applied on top of the hero photo
        heroOverlay: 'linear-gradient(to top, rgba(10,22,40,0.85) 0%, rgba(10,22,40,0.50) 60%, rgba(10,22,40,0.25) 100%)',
        accent: '#00d4ff',   // CTA button background
        accentText: '#0a1628', // text on the CTA button
        sectionBg: '#ffffff',
        sectionAlt: '#f4f7fb',
        textPrimary: '#0a1628',
        textSecondary: '#1a3a5c',
        swatch: ['#0a1628', '#00d4ff'],
    },
    sunset: {
        id: 'sunset',
        name: 'Sunset',
        description: 'Warm coral CTA over rich plum — bold and energetic.',
        heroGradient: 'linear-gradient(135deg, #1a0b2e 0%, #3d1456 50%, #6b1d3f 100%)',
        heroOverlay: 'linear-gradient(to top, rgba(26,11,46,0.85) 0%, rgba(26,11,46,0.50) 60%, rgba(26,11,46,0.25) 100%)',
        accent: '#ff6b35',
        accentText: '#ffffff',
        sectionBg: '#ffffff',
        sectionAlt: '#fff5ef',
        textPrimary: '#2b1235',
        textSecondary: '#4a2747',
        swatch: ['#3d1456', '#ff6b35'],
    },
    forest: {
        id: 'forest',
        name: 'Forest',
        description: 'Earthy teal over deep emerald — calming and natural.',
        heroGradient: 'linear-gradient(135deg, #07201d 0%, #0e3b32 55%, #155e4b 100%)',
        heroOverlay: 'linear-gradient(to top, rgba(7,32,29,0.85) 0%, rgba(7,32,29,0.50) 60%, rgba(7,32,29,0.25) 100%)',
        accent: '#5fd6a6',
        accentText: '#07201d',
        sectionBg: '#ffffff',
        sectionAlt: '#eef6f1',
        textPrimary: '#102e29',
        textSecondary: '#26483f',
        swatch: ['#0e3b32', '#5fd6a6'],
    },
    custom: {
        id: 'custom',
        name: 'Custom',
        description: 'Pick any solid color — full creative control.',
        // These are placeholders. When the renderer sees theme==='custom' it
        // pulls the actual color from `profile.custom_hero_color` (or
        // accent_color as a fallback). Helpers below assemble the real
        // gradient/overlay on demand.
        heroGradient: 'linear-gradient(135deg, #0a1628 0%, #1a3a5c 55%, #0b4d6b 100%)',
        heroOverlay: 'linear-gradient(to top, rgba(10,22,40,0.85) 0%, rgba(10,22,40,0.50) 60%, rgba(10,22,40,0.25) 100%)',
        accent: '#00d4ff',
        accentText: '#0a1628',
        sectionBg: '#ffffff',
        sectionAlt: '#f4f7fb',
        textPrimary: '#0a1628',
        textSecondary: '#1a3a5c',
        // Two-stop swatch shown in the picker card preview — overridden at runtime
        swatch: ['#0a1628', '#00d4ff'],
        isCustom: true,
    },
};

// Curated palette of "safe" solid colors that read well as a hero background
// + look professional. Each entry is the base color the coach picks; the
// runtime `resolveTheme()` helper derives a darker shade for the gradient
// and a contrast text color.
export const CUSTOM_COLOR_PRESETS = [
    { id: 'midnight', name: 'Midnight', color: '#0a1628' },
    { id: 'royal',    name: 'Royal',    color: '#1e3a8a' },
    { id: 'sky',      name: 'Sky',      color: '#0284c7' },
    { id: 'teal',     name: 'Teal',     color: '#0d9488' },
    { id: 'emerald',  name: 'Emerald',  color: '#047857' },
    { id: 'plum',     name: 'Plum',     color: '#581c87' },
    { id: 'coral',    name: 'Coral',    color: '#dc2626' },
    { id: 'charcoal', name: 'Charcoal', color: '#374151' },
];

// --- Color math helpers used by the custom theme renderer -----------------

/** Clamp a number into [min, max]. */
const _clamp = (v, lo, hi) => Math.max(lo, Math.min(hi, v));

/** Parse #rrggbb → {r,g,b}. Returns null if input is malformed. */
function _hexToRgb(hex) {
    if (typeof hex !== 'string') return null;
    const m = hex.trim().replace(/^#/, '');
    if (!/^[0-9a-fA-F]{6}$/.test(m)) return null;
    return {
        r: parseInt(m.slice(0, 2), 16),
        g: parseInt(m.slice(2, 4), 16),
        b: parseInt(m.slice(4, 6), 16),
    };
}

/** {r,g,b} → "#rrggbb". */
function _rgbToHex({ r, g, b }) {
    const h = (n) => _clamp(Math.round(n), 0, 255).toString(16).padStart(2, '0');
    return `#${h(r)}${h(g)}${h(b)}`;
}

/** Mix two RGB colors. `t` 0..1 — 0 = a, 1 = b. */
function _mix(a, b, t) {
    return { r: a.r + (b.r - a.r) * t, g: a.g + (b.g - a.g) * t, b: a.b + (b.b - a.b) * t };
}

/** Relative luminance via WCAG formula — used to pick black/white text. */
function _luminance({ r, g, b }) {
    const lin = (c) => {
        const s = c / 255;
        return s <= 0.03928 ? s / 12.92 : Math.pow((s + 0.055) / 1.055, 2.4);
    };
    return 0.2126 * lin(r) + 0.7152 * lin(g) + 0.0722 * lin(b);
}

/** Pick black or near-black/white for text overlaid on the given color. */
function _readableTextOn(rgb) {
    return _luminance(rgb) > 0.45 ? '#0a1628' : '#ffffff';
}

/**
 * Build a theme object on the fly for the "custom" theme based on the
 * coach's chosen base color. We darken the base color for the gradient
 * top so white hero text always reads, and we derive a contrasting
 * accent text color for the CTA button.
 *
 * `accentOverride` (optional) lets the coach independently override the
 * CTA button color while keeping the hero background from the theme —
 * supports the A+C combo from the picker UX.
 */
export function resolveTheme(themeId, customColor, accentOverride) {
    const base = getTheme(themeId);
    if (!base.isCustom && !accentOverride) return base;

    const next = { ...base };

    if (base.isCustom) {
        const rgb = _hexToRgb(customColor) || _hexToRgb('#0a1628');
        const dark = _mix(rgb, { r: 0, g: 0, b: 0 }, 0.65); // 65% toward black
        const mid = _mix(rgb, { r: 0, g: 0, b: 0 }, 0.35);  // 35% toward black
        const darkHex = _rgbToHex(dark);
        const midHex = _rgbToHex(mid);
        const baseHex = _rgbToHex(rgb);
        next.heroGradient = `linear-gradient(135deg, ${darkHex} 0%, ${midHex} 55%, ${baseHex} 100%)`;
        // Solid dark overlay so the photo (when present) still has readable text
        next.heroOverlay = `linear-gradient(to top, ${darkHex}d9 0%, ${darkHex}80 60%, ${darkHex}40 100%)`;
        // Default the accent to the chosen color (will be overridden below if set)
        next.accent = baseHex;
        next.accentText = _readableTextOn(rgb);
        next.swatch = [darkHex, baseHex];
    }

    // Accent override — applies to ALL themes, not just custom. This is the
    // "C" path from the A+C combo: keep the theme's hero, change only the CTA.
    if (accentOverride && _hexToRgb(accentOverride)) {
        const aRgb = _hexToRgb(accentOverride);
        next.accent = accentOverride;
        next.accentText = _readableTextOn(aRgb);
    }

    return next;
}

export const LANDING_FONTS = {
    display: {
        id: 'display',
        name: 'Playful',
        description: 'Friendly + rounded (Sora)',
        // Inherits the SplashPad "font-display" stack — already loaded globally
        headingClass: 'font-display',
        bodyClass: 'font-sans',
        previewCss: "'Sora', system-ui, sans-serif",
    },
    classic: {
        id: 'classic',
        name: 'Classic',
        description: 'Elegant serif (Playfair Display)',
        headingClass: 'font-classic',
        bodyClass: 'font-sans',
        previewCss: "'Playfair Display', Georgia, serif",
    },
    modern: {
        id: 'modern',
        name: 'Modern',
        description: 'Clean geometric (Montserrat)',
        headingClass: 'font-modern',
        bodyClass: 'font-sans',
        previewCss: "'Montserrat', Inter, sans-serif",
    },
};

export const getTheme = (id) => LANDING_THEMES[id] || LANDING_THEMES.ocean;
export const getFont = (id) => LANDING_FONTS[id] || LANDING_FONTS.display;
